/**
 * @file ChannelCardFields.tsx
 * @description Поля/секции карточки канала для ChannelCardDrawer (phase17.1, шаги 9-11).
 *
 * OverridableTextField — overridable-поле с бейджем наследуется/переопределено
 * и «↩ к базовому». LegalSection — neverOverride (C2) read-only с 🔒.
 * MarketplaceSection — статус синка/модерации/категория (только MARKETPLACE).
 * GalleryInfo — источник галереи (канал/товар, OPEN-1).
 */

import { Lock, RotateCcw } from "lucide-react";
import type { ReactNode } from "react";

import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Textarea } from "../../../../components/ui/textarea";
import type { ChannelCard } from "../../../../lib/api-channel-listings";

/** Бейдж источника значения. */
function OriginBadge({ overridden }: { overridden: boolean }) {
  return overridden ? (
    <Badge variant="default" className="py-0">переопределено</Badge>
  ) : (
    <Badge variant="outline" className="py-0">наследуется</Badge>
  );
}

export function OverridableTextField({
  id,
  label,
  baseHint,
  value,
  multiline,
  onChange,
  onReset,
}: {
  id: string;
  label: string;
  /** Базовое значение (для серого плейсхолдера, когда наследуется). */
  baseHint: string | null;
  value: string;
  multiline?: boolean;
  onChange: (v: string) => void;
  onReset: () => void;
}) {
  const overridden = value.trim() !== "";
  const placeholder = baseHint ? baseHint : "как у товара";
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id} className="text-xs">{label}</Label>
        <div className="flex items-center gap-1.5">
          <OriginBadge overridden={overridden} />
          {overridden ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 px-1.5 text-xs gap-1"
              onClick={onReset}
              title="Сбросить к базовому (применится при сохранении)"
            >
              <RotateCcw className="size-3" /> к базовому
            </Button>
          ) : null}
        </div>
      </div>
      {multiline ? (
        <Textarea id={id} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} rows={3} />
      ) : (
        <Input id={id} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

/** Строка read-only юр-поля. */
function LegalRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm py-1">
      <span className="text-on-surface-variant">{label}</span>
      <span className="text-on-surface text-right flex items-center gap-1.5 min-w-0">
        <span className="truncate">{value ?? "—"}</span>
        <Lock className="size-3 text-on-surface-variant shrink-0" />
      </span>
    </div>
  );
}

export function LegalSection({ legal }: { legal: ChannelCard["legal"] }) {
  return (
    <section className="rounded-lg bg-surface-low p-3">
      <h4 className="type-caption font-medium text-on-surface-variant mb-1 flex items-center gap-1.5">
        <Lock className="size-3.5" /> Обязательно по закону
      </h4>
      <p className="text-xs text-on-surface-variant mb-2">Едино для всех каналов, не переопределяется.</p>
      <LegalRow label="Состав" value={legal.composition} />
      <LegalRow label="Срок годности (дн)" value={legal.shelfLifeDays} />
      <LegalRow label="Условия хранения" value={legal.storageConditions} />
      <LegalRow label="Аллергены" value={legal.allergens.length ? legal.allergens.join(", ") : "—"} />
      <LegalRow label="№ декларации" value={legal.declarationNumber} />
      {legal.badWarning ? <LegalRow label="БАД-предупреждение" value="да" /> : null}
      {legal.ageRestriction ? <LegalRow label="Возраст" value={`${legal.ageRestriction}+`} /> : null}
    </section>
  );
}

export function MarketplaceSection({ mp }: { mp: NonNullable<ChannelCard["marketplace"]> }) {
  return (
    <section className="rounded-lg border border-outline-variant p-3">
      <h4 className="type-caption font-medium text-on-surface-variant mb-2">Маркетплейс</h4>
      <div className="flex flex-col gap-1 text-sm">
        <LegalRowLike label="externalItemId" value={mp.externalItemId ?? "—"} />
        <LegalRowLike label="Синхронизация" value={mp.syncStatus} />
        <LegalRowLike label="Модерация" value={mp.moderationStatus} />
        {mp.lastError ? <LegalRowLike label="Ошибка" value={mp.lastError} danger /> : null}
      </div>
      <p className="text-xs text-on-surface-variant mt-2">
        Категория площадки и обязательные атрибуты настраиваются при подключении интеграции.
      </p>
    </section>
  );
}

function LegalRowLike({ label, value, danger }: { label: string; value: ReactNode; danger?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-on-surface-variant text-xs">{label}</span>
      <span className={`text-right text-xs ${danger ? "text-destructive" : "text-on-surface"}`}>{value}</span>
    </div>
  );
}

export function GalleryInfo({ media }: { media: ChannelCard["media"] }) {
  const isChannel = media.source === "channel";
  return (
    <section className="rounded-lg border border-outline-variant p-3">
      <div className="flex items-center justify-between gap-2">
        <h4 className="type-caption font-medium text-on-surface-variant">Галерея</h4>
        <OriginBadge overridden={isChannel} />
      </div>
      <p className="text-sm text-on-surface mt-1">
        {isChannel
          ? `Свой набор канала — ${media.items.length} фото.`
          : `Наследует галерею товара — ${media.items.length} фото.`}
      </p>
    </section>
  );
}
